/**
 * /app/ingredientes/:productId/composicion — BOM: editor de receta para un producto.
 * Permite agregar/editar/eliminar líneas de receta (ingrediente o sub-receta) con cantidad.
 * Muestra disponibilidad derivada calculada por el motor BOM.
 * Gateado por inventory_bom.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { DataTable, EmptyState, Header, Loading } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { CrudEditorShell } from "@/components/workspace/CrudEditorShell";
import { FieldLabel } from "@/components/help/FieldHelpTip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ArrowLeft, CheckCircle2, Search } from "lucide-react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";

export const Route = createFileRoute("/app/ingredientes/$productId/composicion")({
  component: BomEditorPage,
  head: () => ({ meta: [{ title: "Composición (BOM)" }] }),
});

interface RecipeLine {
  id: string;
  ingredientId: string | null;
  ingredientNombre?: string;
  ingredientUnidad?: string;
  subRecipeProductId: string | null;
  subRecipeNombre?: string;
  qtyRequired: number;
}
interface Ingredient {
  id: string;
  nombre: string;
  unidadBase: string;
  stockActual: number;
}

function BomEditorPage() {
  const { productId } = Route.useParams();
  const tid = useWorkspaceTenantId();

  const [lines, setLines] = useState<RecipeLine[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [availability, setAvailability] = useState<number | null>(null);
  const [productNombre, setProductNombre] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);

  // Form para nueva línea
  const [newLine, setNewLine] = useState({ ingredientId: "", qty: "1" });
  const [showAdd, setShowAdd] = useState(false);
  const [ingSearch, setIngSearch] = useState("");

  const load = async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [rr, ir, ar, pr] = await Promise.all([
        apiGetJson<{ lines?: RecipeLine[]; _lock?: PlanApiErrorPayload }>(
          `/api/recipes?productId=${productId}`,
          tid,
        ),
        apiGetJson<{ ingredients?: Ingredient[] }>(`/api/ingredients`, tid),
        apiGetJson<{ availability?: { availableUnits?: number } }>(`/api/recipes?productId=${productId}&action=availability`, tid),
        apiGetJson<{ products?: Array<{ id: string; nombre: string }> }>(
          `/api/products?limit=1&id=${productId}`,
          tid,
        ),
      ]);
      if (rr._lock) {
        setLock(rr._lock);
        return;
      }
      setLines(rr.lines ?? []);
      setIngredients(ir.ingredients ?? []);
      setAvailability(
        ar.availability?.availableUnits != null && ar.availability.availableUnits < Number.MAX_SAFE_INTEGER / 2
          ? ar.availability.availableUnits
          : null,
      );
      if (pr.products?.[0]) setProductNombre(pr.products[0].nombre);
    } catch {
      toast.error("Error al cargar composición");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tid, productId]);

  const handleAddLine = async () => {
    if (!newLine.ingredientId || !newLine.qty) return;
    const qty = parseFloat(newLine.qty);
    if (!qty || qty <= 0) {
      toast.error("Cantidad inválida");
      return;
    }

    const updatedLines = [
      ...lines,
      {
        id: `new_${Date.now()}`,
        ingredientId: newLine.ingredientId,
        subRecipeProductId: null,
        qtyRequired: qty,
      },
    ];
    await saveLines(updatedLines);
    setNewLine({ ingredientId: "", qty: "1" });
    setShowAdd(false);
  };

  const handleRemoveLine = async (lineId: string) => {
    const updatedLines = lines.filter((l) => l.id !== lineId);
    await saveLines(updatedLines);
  };

  const handleUpdateQty = async (lineId: string, qty: number) => {
    if (qty <= 0) return;
    const updatedLines = lines.map((l) => (l.id === lineId ? { ...l, qtyRequired: qty } : l));
    await saveLines(updatedLines);
  };

  const saveLines = async (updated: RecipeLine[]) => {
    if (!tid) return;
    setSaving(true);
    try {
      await apiSendJson(
        `/api/recipes?productId=${encodeURIComponent(productId)}`,
        "PUT",
        {
          lines: updated.map((l) => {
            const ing = ingredients.find((i) => i.id === l.ingredientId);
            return {
              ingredientId: l.ingredientId,
              subRecipeProductId: l.subRecipeProductId,
              qtyRequired: l.qtyRequired,
              unit: (ing?.unidadBase as "GRAMOS" | "KILOS" | "MILILITROS" | "LITROS" | "UNIDADES") || "UNIDADES",
            };
          }),
        },
        tid,
      );
      toast.success("Receta guardada");
      load();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (lock) return <FeatureLockedState payload={lock} />;

  const availableIngredients = ingredients.filter((i) => !lines.some((l) => l.ingredientId === i.id));

  const filteredIngredients = (() => {
    const q = ingSearch.trim().toLowerCase();
    if (!q) return availableIngredients;
    return availableIngredients.filter((i) =>
      i.nombre.toLowerCase().includes(q) || i.unidadBase.toLowerCase().includes(q),
    );
  })();

  const columns: DataTableColumn<RecipeLine>[] = [
    {
      key: "ingrediente",
      header: "Ingrediente",
      className: "font-medium",
      render: (line) => {
        const ing = ingredients.find((i) => i.id === line.ingredientId);
        return (
          line.ingredientNombre ??
          ing?.nombre ??
          (line.subRecipeNombre ? `Sub-receta: ${line.subRecipeNombre}` : line.ingredientId)
        );
      },
    },
    {
      key: "unidad",
      header: "Unidad",
      className: "text-xs text-muted-foreground",
      render: (line) => {
        const ing = ingredients.find((i) => i.id === line.ingredientId);
        return line.ingredientUnidad ?? ing?.unidadBase ?? "—";
      },
    },
    {
      key: "stock",
      header: "Stock actual",
      className: "text-right tabular-nums text-muted-foreground",
      render: (line) => {
        const ing = ingredients.find((i) => i.id === line.ingredientId);
        return ing ? ing.stockActual.toLocaleString("es-AR") : "—";
      },
    },
    {
      key: "qty",
      header: "Cantidad x unidad",
      className: "text-right",
      render: (line) => (
        <input
          type="number"
          min="0.001"
          step="0.001"
          defaultValue={line.qtyRequired}
          onBlur={(e) => {
            const v = parseFloat(e.target.value);
            if (v > 0 && v !== line.qtyRequired) handleUpdateQty(line.id, v);
          }}
          className="w-20 rounded-lg border border-border bg-background px-2 py-1 text-right text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-primary/40"
        />
      ),
    },
    {
      key: "rinde",
      header: "Rinde",
      className: "text-right tabular-nums",
      render: (line) => {
        const ing = ingredients.find((i) => i.id === line.ingredientId);
        const rinde = ing ? Math.floor(ing.stockActual / line.qtyRequired) : null;
        return rinde !== null ? (
          <span className={rinde > 0 ? "text-green-400" : "text-red-400"}>{rinde}</span>
        ) : (
          "—"
        );
      },
    },
    {
      key: "actions",
      header: "",
      className: "w-8",
      render: (line) => (
        <button
          onClick={() => handleRemoveLine(line.id)}
          disabled={saving}
          className="text-muted-foreground hover:text-red-400"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <Header
        title={`Composición — ${productNombre || productId}`}
        subtitle="Ingredientes o materiales y cantidades por unidad"
      />
      {loading ? (
        <Loading variant="list" />
      ) : (
      <ClinicalPageShell offset="none" className="pt-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Link
          to="/app/productos"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Volver a productos
        </Link>
        <Link
          to="/app/ingredientes"
          className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Ver insumos
        </Link>
      </div>

      {lines.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-3 py-3 text-sm text-muted-foreground">
          Todavía no hay insumos en la receta. Agregá ingredientes abajo: al vender este producto se
          descontarán, y si falta stock no se podrá vender.
        </div>
      ) : null}

      {/* Disponibilidad derivada */}
      {availability !== null && (
        <div
          className={`flex items-center gap-3 rounded-xl border px-4 py-3
          ${availability > 0 ? "border-green-500/30 bg-green-500/10" : "border-red-500/30 bg-red-500/10"}`}
        >
          <CheckCircle2 className={`h-5 w-5 ${availability > 0 ? "text-green-400" : "text-red-400"}`} />
          <div>
            <p className="text-sm font-semibold">
              {availability > 0 ? `Se pueden producir ${availability} unidades` : "Sin stock para producir"}
            </p>
            <p className="text-xs text-muted-foreground">Calculado en base al ingrediente más escaso</p>
          </div>
          <Badge
            variant="outline"
            className={`ml-auto text-lg font-bold tabular-nums ${availability > 0 ? "text-green-400" : "text-red-400"}`}
          >
            {availability}
          </Badge>
        </div>
      )}

      <CrudEditorShell
        open={showAdd}
        onOpenChange={(open) => {
          setShowAdd(open);
          if (!open) { setNewLine({ ingredientId: "", qty: "1" }); setIngSearch(""); }
        }}
        title="Agregar ingrediente a la receta"
        size="md"
        list={
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setShowAdd(true)} disabled={availableIngredients.length === 0}>
                <Plus className="mr-1.5 h-4 w-4" /> Agregar ingrediente
              </Button>
            </div>
            <DataTable
              columns={columns}
              rows={lines}
              getRowKey={(line) => line.id}
              emptyState={
                <EmptyState title="Sin ingredientes en la receta" description="Agregá uno con el botón de arriba." />
              }
            />
          </div>
        }
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setShowAdd(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => void handleAddLine()}
              disabled={!newLine.ingredientId || saving}
            >
              {saving ? "Guardando…" : "Agregar"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <FieldLabel tipKey="ing.name" required>
              Ingrediente
            </FieldLabel>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={ingSearch}
                onChange={(e) => setIngSearch(e.target.value)}
                placeholder="Buscar ingrediente…"
                className="pl-8 h-8 text-sm"
              />
            </div>
            <div className="max-h-44 overflow-y-auto rounded-md border border-input divide-y divide-border/40">
              {filteredIngredients.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                  {ingSearch ? "Sin coincidencias" : "Todos los ingredientes ya están en la receta"}
                </p>
              ) : (
                filteredIngredients.map((i) => (
                  <label
                    key={i.id}
                    className={[
                      "flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-muted/50",
                      newLine.ingredientId === i.id ? "bg-primary/5 font-medium" : "",
                    ].join(" ")}
                  >
                    <input
                      type="radio"
                      name="ingredientId"
                      value={i.id}
                      checked={newLine.ingredientId === i.id}
                      onChange={() => setNewLine((f) => ({ ...f, ingredientId: i.id }))}
                      className="h-3.5 w-3.5"
                    />
                    <span className="flex-1 truncate">{i.nombre}</span>
                    <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                      {i.unidadBase} · {i.stockActual.toLocaleString("es-AR")}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <FieldLabel tipKey="ing.unit" required>
              Cantidad por unidad
            </FieldLabel>
            <Input
              type="number"
              min={0.001}
              step={0.001}
              value={newLine.qty}
              onChange={(e) => setNewLine((f) => ({ ...f, qty: e.target.value }))}
              className="text-right tabular-nums"
            />
          </div>
        </div>
      </CrudEditorShell>
      </ClinicalPageShell>
      )}
    </div>
  );
}
